<template>
  <div class="project-model-edit-form">
    <div class="primary-lighten-1 flex w-full flex-col gap-y-2 rounded-md p-2">
      <label>
        <span class="sr-only block text-sm">Projectnaam</span>
        <InputText
          v-model="project.name"
          placeholder="Projectnaam"
          ref="inputName"
          @blur="v$.name.$touch()"
          :errors="v$.name.$errors"
        >
          <template #before-input>
            <FontAwesomeIcon size="sm" icon="file-contract" title="Projectnaam" />
          </template>
        </InputText>
      </label>

      <label>
        <span class="sr-only block text-sm">Financieel verantwoordelijke</span>
        <KnownBusinessEntityContactCombobox
          v-model="project.executor"
          ref="inputExecutor"
          input-placeholder="Financieel verantwoordelijke"
          grow
          :allowed-roles="[RoleTypes.FINANCIAL_OFFICER]"
          :selected-item-display-fn="getDisplayValue"
          :errors="v$.executor.$errors"
          @focusout="v$.executor.$touch()"
          @item-created="onExecutorCreated"
        >
          <template #before-input>
            <FontAwesomeIcon size="sm" icon="coins" title="Financieel verantwoordelijke" />
          </template>
        </KnownBusinessEntityContactCombobox>
      </label>

      <label>
        <span class="sr-only block text-sm">Contactpersoon</span>
        <KnownBusinessEntityContactCombobox
          v-model="project.contact"
          ref="inputPerson"
          input-placeholder="Contactpersoon"
          grow
          :selected-item-display-fn="getDisplayValue"
          :errors="v$.contact.$errors"
          @focusout="v$.contact.$touch()"
          @keydown.tab="onContactTabOut"
          @is-creating-new-item="isCreatingNewContact = $event"
        >
          <template #before-input>
            <FontAwesomeIcon size="sm" icon="address-card" title="Contactpersoon" />
          </template>
        </KnownBusinessEntityContactCombobox>
      </label>

      <div>
        <span class="block text-sm select-none">Interne projectopmerking</span>
        <div class="relative h-full grow">
          <LocationMentionTextarea
            autosize
            v-model="project.descriptionPlanner"
            placeholder="Beschrijf interne project afspraken of bijzonderheden"
            ref="plannerDescriptionTextArea"
            :class="[project.fileBucket.fileRelations.length > 0 ? 'pr-12' : 'pr-5']"
            @file-trigger="plannerDescriptionFileUploader?.open()"
          >
            <template #overlay>
              <FileUploader
                ref="plannerDescriptionFileUploader"
                :context="FileUploadContexts.PLANNING"
                :file-bucket="project.fileBucket"
                show-file-count
                icons-class="absolute bottom-1 right-1 surface"
                :override-is-uploading="isUploading"
                @file-uploaded="
                  (fileRelation) => {
                    onFileUploaded(fileRelation)
                    plannerDescriptionTextArea?.insertUploadedFile(fileRelation)
                  }
                "
                @is-uploading="isUploading = $event"
              />
            </template>
          </LocationMentionTextarea>
          <FileDropzone
            :context="FileUploadContexts.PLANNING"
            :file-bucket="project.fileBucket"
            @file-uploaded="onFileUploaded"
            @is-uploading="isUploading = $event"
          />
        </div>
      </div>

      <div>
        <span class="block text-sm select-none">Projectopmerking voor dienstverleners</span>
        <div class="relative h-full grow">
          <LocationMentionTextarea
            autosize
            v-model="project.descriptionServiceProvider"
            placeholder="Beschrijf wat dienstverleners van het project moeten weten"
            ref="serviceProviderDescriptionTextArea"
            :class="[
              project.fileBucketServiceProviderAttachments.fileRelations.length > 0
                ? 'pr-12'
                : 'pr-5'
            ]"
            @file-trigger="serviceProviderDescriptionFileUploader?.open()"
          >
            <template #overlay>
              <FileUploader
                ref="serviceProviderDescriptionFileUploader"
                :context="FileUploadContexts.PLANNING"
                :file-bucket="project.fileBucketServiceProviderAttachments"
                show-file-count
                icons-class="absolute bottom-1 right-1 surface"
                :override-is-uploading="isServiceProviderDescriptionUploading"
                @file-uploaded="
                  (fileRelation) => {
                    onServiceProviderFileUploaded(fileRelation)
                    serviceProviderDescriptionTextArea?.insertUploadedFile(fileRelation)
                  }
                "
                @is-uploading="isServiceProviderDescriptionUploading = $event"
              />
            </template>
          </LocationMentionTextarea>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import InputText from "@/components/inputs/InputText.vue"
import { inject, onMounted, ref, useTemplateRef } from "vue"
import type { Project } from "@/models/project.model"
import { requiredValidator } from "@/helper/validation/validators"
import LocationMentionTextarea from "@/components/inputs/LocationMentionTextarea.vue"
import { upperFirst } from "lodash-es"
import { useFormConfigurator } from "@/composables/form/form-configurator"
import { ProjectController } from "@/controllers/model-controllers/project-controller"
import { isUniqueNameAndExecutorValidator } from "@/helper/validation/project-validation"
import KnownBusinessEntityContactCombobox from "@/components/inputs/KnownBusinessEntityContactCombobox.vue"
import { formFocusHack } from "@/helper/form-helper"
import { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"
import { FileUploadContexts } from "@/enums/file-upload-contexts"
import FileDropzone from "@/components/file/FileDropzone.vue"
import type { FileRelation } from "@/models/file/file-relation.model"
import FileUploader from "@/components/file/FileUploader.vue"
import { RoleTypes } from "@/enums/role-types.ts"
import { Person } from "@/models/person.model"

const inputName = ref()
const plannerDescriptionTextArea = useTemplateRef("plannerDescriptionTextArea")
const plannerDescriptionFileUploader = useTemplateRef("plannerDescriptionFileUploader")
const serviceProviderDescriptionTextArea = useTemplateRef("serviceProviderDescriptionTextArea")
const serviceProviderDescriptionFileUploader = useTemplateRef(
  "serviceProviderDescriptionFileUploader"
)

const rules = {
  name: { requiredValidator, isUniqueNameAndExecutorValidator },
  executor: { requiredValidator },
  contact: { requiredValidator }
}

const isUploading = ref(false)
const isServiceProviderDescriptionUploading = ref(false)

function onFileUploaded(fileRelation: FileRelation) {
  project.value.fileBucket.fileRelations.push(fileRelation)
}

function onServiceProviderFileUploaded(fileRelation: FileRelation) {
  project.value.fileBucketServiceProviderAttachments.fileRelations.push(fileRelation)
}

const controller: ProjectController = inject("projectController")!
const { model: project, initialText, v$ } = useFormConfigurator<Project>({ controller, rules })

onMounted(() => {
  if (initialText) {
    project.value.name = upperFirst(initialText)
  }
  formFocusHack(inputName, project, "name")
})

function getDisplayValue(kbec: KnownBusinessEntityContact) {
  return `${kbec.person?.fullName} | ${kbec.knownBusinessEntityName}`
}

const isCreatingNewContact = ref(false)
function onContactTabOut() {
  if (isCreatingNewContact.value) {
    // In case new item option was selected using tab key, prevent auto select behavior. Else the selected value
    // will contaminate the PersonModelEditForm.
    return
  }
  // If contact is left empty, match it to the executor
  if (!project.value.contact && project.value.executor) {
    project.value.contact = project.value.executor
  }
}

function onExecutorCreated(personOrKBEC: KnownBusinessEntityContact | Person) {
  if (personOrKBEC instanceof KnownBusinessEntityContact) {
    project.value.executor = personOrKBEC
  }
}
</script>

<style scoped></style>
